import { useEffect, useState } from "react";
import {Mail, Lock, ShieldCheck, X, Loader2, KeyRound, CheckCircle2} from 'lucide-react';
import toast from "react-hot-toast";
import { lumi } from "@/lib/lumi";
import { saveProfile } from "@/hooks/useAuth";
import { emailCandidate_AccountCreated } from "@/lib/emails";

interface CandidateSignupModalProps {
  isOpen: boolean;
  email: string;
  fullName: string;
  phone?: string;
  onClose: () => void;
  /**
   * Appelé une fois que le compte est créé et que la session Lumi est active.
   * `userId` est l'identifiant Lumi du candidat fraîchement inscrit, à propager
   * à `createApplication` pour relier la candidature au compte.
   */
  onSuccess: (userId: string) => Promise<void> | void;
}

/**
 * Modale d'inscription rapide pour candidat à une offre.
 *
 * Flux :
 *  1. Pré-rempli avec l'email et le nom du formulaire de candidature.
 *  2. Le candidat choisit un mot de passe + confirmation.
 *  3. Il clique "Envoyer le code" → sendOtp({ type: 'register' }).
 *  4. Il saisit le code reçu par email puis valide → signUpWithPassword().
 *  5. La SDK Lumi connecte automatiquement le candidat ; `onSuccess` est appelé
 *     avec `lumi.auth.user.userId` pour finaliser la candidature.
 */
export default function CandidateSignupModal({
  isOpen,
  email,
  fullName,
  phone,
  onClose,
  onSuccess,
}: CandidateSignupModalProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset complet quand la modale est fermée puis rouverte
  useEffect(() => {
    if (!isOpen) {
      setPassword("");
      setConfirmPassword("");
      setOtp("");
      setOtpSent(false);
      setCountdown(0);
      setError(null);
      setSendingOtp(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Décompte du cooldown OTP (60s)
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  if (!isOpen) return null;

  const handleSendOtp = async () => {
    setError(null);
    if (!email) {
      setError("Email manquant : merci de renseigner votre email avant de demander un code.");
      return;
    }
    if (password.length < 6) {
      setError("Votre mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSendingOtp(true);
    try {
      await lumi.auth.sendOtp({ email, type: "register" });
      setOtpSent(true);
      setCountdown(60);
      toast.success("Code de vérification envoyé par email.");
    } catch (e: unknown) {
      const err = e as { code?: number; message?: string };
      // Si l'utilisateur existe déjà, on guide vers la connexion
      if (err?.code === 2202 || /already|exist/i.test(err?.message || "")) {
        setError("Un compte existe déjà avec cet email. Connectez-vous depuis la page Connexion pour postuler.");
      } else {
        setError(err?.message || "Impossible d'envoyer le code. Merci de réessayer.");
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!otpSent) {
      setError("Veuillez d'abord demander un code de vérification.");
      return;
    }
    const cleanedOtp = otp.replace(/\D/g, "");
    if (!cleanedOtp || cleanedOtp.length < 4) {
      setError("Merci de saisir le code de vérification reçu par email.");
      return;
    }
    setSubmitting(true);
    try {
      await lumi.auth.signUpWithPassword({
        email,
        password,
        otp: cleanedOtp,
        userName: fullName || email.split("@")[0],
      });

      // La SDK Lumi se connecte automatiquement après l'inscription,
      // mais on déclenche un signIn de secours au cas où onAuthChange tarderait.
      try {
        await lumi.auth.signInWithPassword({ email, password });
      } catch {
        // Déjà connecté → on ignore
      }

      // Sauvegarde le profil métier "candidat" (rôle + métadonnées) côté local
      saveProfile({
        email,
        role: "candidate",
        fullName,
        phone,
        createdAt: new Date().toISOString(),
      });

      // Email 7 — Bienvenue compte candidat (best-effort)
      void emailCandidate_AccountCreated({
        candidateEmail: email,
        candidateName: fullName,
        role: "candidate",
      });

      const userId = lumi.auth.user?.userId;
      if (!userId) {
        throw new Error("Compte créé mais session non disponible. Merci de vous reconnecter.");
      }

      toast.success("Compte créé ! Envoi de votre candidature…");
      await onSuccess(userId);
    } catch (e: unknown) {
      const err = e as { code?: number; message?: string };
      // Code OTP invalide / expiré (Lumi: 2201)
      if (err?.code === 2201 || /otp|code/i.test(err?.message || "")) {
        setError("Code de vérification invalide ou expiré. Demandez un nouveau code.");
      } else {
        setError(err?.message || "Échec de la création du compte. Merci de réessayer.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-blue-600 px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div className="text-white pr-4">
            <p className="text-[11px] font-bold tracking-widest uppercase opacity-80 mb-1">
              Étape requise
            </p>
            <h2 id="signup-title" className="text-lg font-bold leading-tight">
              Créez votre compte candidat pour finaliser votre candidature
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Fermer"
            className="text-white/80 hover:text-white transition disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSignup} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Pour suivre votre candidature et recevoir les réponses du recruteur,
            un compte candidat est créé avec votre email. Un <strong>code de vérification</strong>{" "}
            vous est envoyé pour confirmer votre identité.
          </p>

          {/* Email (lecture seule, repris du formulaire) */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={email}
                readOnly
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-700"
              />
            </div>
          </div>

          {/* Mot de passe */}
          <div>
            <label htmlFor="signup-password" className="block text-xs font-semibold text-gray-700 mb-1">
              Mot de passe <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="signup-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 caractères"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label htmlFor="signup-confirm" className="block text-xs font-semibold text-gray-700 mb-1">
              Confirmation du mot de passe <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <ShieldCheck className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="signup-confirm"
                type="password"
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          {/* Bouton envoi OTP */}
          <button
            type="button"
            onClick={handleSendOtp}
            disabled={sendingOtp || countdown > 0 || !password || !confirmPassword}
            className="w-full inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingOtp ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <KeyRound className="w-4 h-4" />
            )}
            {countdown > 0
              ? `Renvoyer le code dans ${countdown}s`
              : otpSent
              ? "Renvoyer le code de vérification"
              : "Envoyer le code de vérification"}
          </button>

          {/* OTP input (visible une fois le code envoyé) */}
          {otpSent && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <p className="text-[11px] font-semibold text-blue-900 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Code envoyé à <span className="font-bold">{email}</span>
              </p>
              <label htmlFor="signup-otp" className="block text-xs font-semibold text-gray-700">
                Code de vérification <span className="text-red-500">*</span>
              </label>
              <input
                id="signup-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Ex : 123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none tracking-widest font-mono"
              />
              <p className="text-[11px] text-gray-600">
                Vérifiez votre boîte mail (et vos spams). Le code est valable quelques minutes.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-xs font-medium rounded-lg p-2.5">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !otpSent}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Création du compte…
              </>
            ) : (
              "Créer mon compte et envoyer ma candidature"
            )}
          </button>

          <p className="text-[11px] text-gray-500 text-center">
            En créant votre compte, vous acceptez nos CGU et notre politique de confidentialité.
          </p>
        </form>
      </div>
    </div>
  );
}
